<template>
  <span ref="root" class="jss-column-manager relative inline-block text-left">
    <button type="button" class="admin-button--secondary" @click.stop="toggle">
      <i class="fa fa-table-columns mr-1"></i>Spalten
    </button>
    <div v-if="open" class="jss-column-panel" @click.stop>
      <div class="flex gap-4">
        <div>
          <div class="font-bold mb-1 whitespace-nowrap">Sichtbar</div>
          <ul ref="visibleList" class="jss-col-list">
            <li v-for="c in visibleItems" :key="c.field" :data-id="c.field" @click="moveToHidden(c.field)">
              {{ c.title }}
            </li>
            <li v-if="visibleItems.length === 0" class="jss-col-empty" data-id="">(keine)</li>
          </ul>
        </div>
        <div>
          <div class="font-bold mb-1 whitespace-nowrap">Ausgeblendet</div>
          <ul ref="hiddenList" class="jss-col-list">
            <li v-for="c in hiddenItems" :key="c.field" :data-id="c.field" @click="moveToVisible(c.field)">
              {{ c.title }}
            </li>
            <li v-if="hiddenItems.length === 0" class="jss-col-empty" data-id="">(keine)</li>
          </ul>
        </div>
      </div>
      <div class="flex justify-between mt-3">
        <button type="button" class="admin-button--ghost admin-button--sm" @click="reset">
          Zurücksetzen
        </button>
        <button type="button" class="admin-button--secondary admin-button--sm" @click="open = false">
          Fertig
        </button>
      </div>
    </div>
  </span>
</template>

<script>
import Sortable from "sortablejs";
import { resolveColumnState, ALWAYS_HIDDEN_COLUMNS } from "@/lib/jssColumns";

// Column manager for the JSS grids: a "Spalten" button that opens a panel
// with two lists. Columns can be dragged vertically inside "Sichtbar" to
// reorder them, and between "Sichtbar"/"Ausgeblendet" to show/hide them.
// Clicking an item also toggles its list (fallback for touch devices).
// Every change is emitted as { order: [field...], hidden: [field...] }.
export default {
  name: "JssColumnManager",
  props: {
    // All manageable columns in default order: [{ field, title }]
    columns: { type: Array, required: true },
    // Field names of the currently visible columns, in order
    order: { type: Array, default: () => [] },
    // Field names of the currently hidden columns
    hidden: { type: Array, default: () => [] }
  },
  emits: ["change"],
  data() {
    return {
      open: false,
      sortables: [],
      outsideClickHandler: null,
      // Set briefly after a drag ends — suppresses the click that fires on
      // mouseup so a same-list reorder doesn't toggle the item's list.
      justDragged: false
    };
  },
  computed: {
    titleByField() {
      const map = {};
      this.columns.forEach(c => { map[c.field] = c.title });
      return map;
    },
    visibleItems() {
      const state = resolveColumnState(this.columns, { order: this.order, hidden: this.hidden });
      return state.order.map(field => ({ field, title: this.titleByField[field] || field }));
    },
    hiddenItems() {
      const state = resolveColumnState(this.columns, { order: this.order, hidden: this.hidden });
      return state.hidden.map(field => ({ field, title: this.titleByField[field] || field }));
    }
  },
  methods: {
    toggle() {
      this.open = !this.open;
      if (this.open) {
        this.$nextTick(() => this.initSortables());
        this.outsideClickHandler = (e) => {
          if (this.$refs.root && !this.$refs.root.contains(e.target)) {
            this.open = false;
          }
        };
        document.addEventListener("click", this.outsideClickHandler);
      }
    },
    initSortables() {
      this.destroySortables();
      [this.$refs.visibleList, this.$refs.hiddenList].forEach(el => {
        if (!el) return;
        this.sortables.push(new Sortable(el, {
          group: "jss-columns",
          animation: 150,
          draggable: "li[data-id]",
          filter: ".jss-col-empty",
          onEnd: () => {
            this.justDragged = true;
            setTimeout(() => { this.justDragged = false; }, 0);
            this.emitFromDom();
          }
        }));
      });
    },
    destroySortables() {
      this.sortables.forEach(s => { try { s.destroy(); } catch (e) { /* ignore */ } });
      this.sortables = [];
    },
    // Reads the current DOM order of both lists after a drag and emits it.
    emitFromDom() {
      const order = this.sortables[0] ? this.sortables[0].toArray().filter(f => f) : [];
      const hidden = this.sortables[1] ? this.sortables[1].toArray().filter(f => f) : [];
      this.$emit("change", { order, hidden });
    },
    emitState(order, hidden) {
      this.$emit("change", { order, hidden });
    },
    moveToHidden(field) {
      if (this.justDragged) return;
      const order = this.visibleItems.map(c => c.field).filter(f => f !== field);
      const hidden = this.hiddenItems.map(c => c.field).concat([field]);
      this.emitState(order, hidden);
    },
    moveToVisible(field) {
      if (this.justDragged) return;
      const hidden = this.hiddenItems.map(c => c.field).filter(f => f !== field);
      const order = this.visibleItems.map(c => c.field).concat([field]);
      this.emitState(order, hidden);
    },
    reset() {
      this.emitState(this.columns.map(c => c.field).filter(f => !ALWAYS_HIDDEN_COLUMNS.has(f)), []);
    }
  },
  watch: {
    open(isOpen) {
      if (!isOpen) this.destroySortables();
    }
  },
  beforeUnmount() {
    this.destroySortables();
    if (this.outsideClickHandler) {
      document.removeEventListener("click", this.outsideClickHandler);
    }
  }
};
</script>

<style scoped>
.jss-column-panel {
  position: absolute;
  right: 0;
  top: calc(100% + 4px);
  z-index: 40;
  background: #fff;
  border: 1px solid #d1d5db;
  border-radius: 0.5rem;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.15);
  padding: 0.75rem;
  min-width: 22rem;
}

.jss-col-list {
  min-height: 2.5rem;
  min-width: 9.5rem;
  max-height: 20rem;
  overflow-y: auto;
  border: 1px dashed #9ca3af;
  border-radius: 0.375rem;
  padding: 0.25rem;
}

.jss-col-list li {
  padding: 0.3rem 0.5rem;
  margin: 0.2rem 0;
  background: #f3f4f6;
  border: 1px solid #d1d5db;
  border-radius: 0.3rem;
  cursor: grab;
  white-space: nowrap;
  font-size: 0.85rem;
  user-select: none;
}

.jss-col-list li:hover {
  background: #e5e7eb;
}

.jss-col-list li.jss-col-empty {
  background: transparent;
  border: none;
  color: #9ca3af;
  cursor: default;
  font-style: italic;
}
</style>
